import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import {
  ApplicableTestLevel,
  CombinedTaskByWicNumber,
  SkuGroupMappingTable,
  PrimeTestLevelCheckboxState,
  GroupingFilter,
  SkuGroupingFilters,
  Direction,
  FillingActionData,
} from "types/sku-group";
import { SkuTestLevelGroup, Task } from "types/task";
import {
  assignSelectedGroupTestLevel,
  checkQualityFlow,
  convertMappingTableToPrimeTestLevelCheckboxes,
  convertMappingTableToSelectAsPrimeCheckboxes,
  convertMappingTableToSelectedGroups,
  removeAllSelectedPrimeInSelectedGroupTestLevel,
  removeSelectedGroupTestLevel,
  sortDefaultSelectedGroup,
  updatePrimeSkuTestLevelCheckbox,
  updatePrimeTestLevelCheckboxes,
  updateSelectAsPrimeCheckbox,
  updateSelectAsPrimeInSkuGroupMappingTable,
  editSelectAsPrimeSelectedGroups,
} from "services/skuGroupingService";
import { TestLevel } from "types/test-item";
import { entries as _entries } from "lodash";

export interface SkuGroupingState {
  skuGroupingFilters: SkuGroupingFilters;
  combinedTasksByWicNumber: CombinedTaskByWicNumber; // for combining multiple market in grouping list
  initialTasks: Task[]; // for retoring data in select sku prime step
  editMode: boolean;
  isDeletedGrouping: boolean; // for delete a sku in edit mode
  isQualityFlow: boolean;
  openFillingActionModal: boolean;
  fillingActionData: FillingActionData | undefined;
  existingPrimeFilters: Record<string, string[]>; // filters for fetching existing prime in existing sku popup
  applicableTestLevel: ApplicableTestLevel | null; // applicable test level data from api
  skuGroupMappingTable: SkuGroupMappingTable; // logic checking table
  selectedGroups: SkuTestLevelGroup[]; // update base on skuGroupMappingTable changes
  filteredSelectedGroups: SkuTestLevelGroup[]; // record current filtered selected groups state for vertical filling actions
  primeSkuTestLevelCheckboxes: PrimeTestLevelCheckboxState; // update base on skuGroupMappingTable changes
  selectAsPrimeCheckboxes: Record<string, boolean>;
}

const initialState: SkuGroupingState = {
  skuGroupingFilters: {
    filters: {
      colorCode: "",
      sizeCode: "",
      sort: null,
    },
    keyword: "",
  },
  combinedTasksByWicNumber: {},
  initialTasks: [],
  editMode: false,
  isDeletedGrouping: false,
  isQualityFlow: false,
  openFillingActionModal: false,
  fillingActionData: undefined,
  applicableTestLevel: null,
  existingPrimeFilters: {},
  skuGroupMappingTable: {},
  selectedGroups: [],
  filteredSelectedGroups: [],
  primeSkuTestLevelCheckboxes: {},
  selectAsPrimeCheckboxes: {},
};

const skugroupingSlice = createSlice({
  name: "skuGrouping",
  initialState,
  reducers: {
    setOpenFillingActionModal: (state, action: PayloadAction<boolean>) => ({
      ...state,
      openFillingActionModal: action.payload,
    }),
    setFillingActionData: (state, action: PayloadAction<FillingActionData | undefined>) => ({
      ...state,
      fillingActionData: action.payload,
    }),
    setSkuGroupingFilters: (state, action) => ({
      ...state,
      skuGroupingFilters: action.payload,
    }),
    setCombinedTaskByWicNumber: (state, action) => ({
      ...state,
      combinedTasksByWicNumber: action.payload,
    }),
    setInitialTasks: (state, action: PayloadAction<Task[]>) => ({
      ...state,
      initialTasks: [...new Set(action.payload)],
      isQualityFlow: checkQualityFlow(action.payload[0]?.exportMarket as string),
    }),
    setSelectedGroupData: (state, action: PayloadAction<SkuTestLevelGroup[]>) => ({
      ...state,
      selectedGroups: action.payload,
    }),
    setFilteredSelectedGroupData: (state, action: PayloadAction<SkuTestLevelGroup[]>) => ({
      ...state,
      filteredSelectedGroups: action.payload,
    }),
    //* set applicable test level from api
    setApplicableTestLevel: (state, action: PayloadAction<ApplicableTestLevel | null>) => ({
      ...state,
      applicableTestLevel: action.payload,
    }),
    //* set existing prime filters for fetching  prime list
    setExistingPrimeFilters: (state, action: PayloadAction<Record<string, string[]>>) => ({
      ...state,
      existingPrimeFilters: action.payload,
    }),
    //* set is edit sku grouping
    setEditMode: (state, action: PayloadAction<boolean>) => ({
      ...state,
      editMode: action.payload,
    }),
    //* set is deleted sku grouping
    setDeletedGrouping: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isDeletedGrouping: action.payload,
    }),
    //* set mapping table for logic checking
    setSkuGroupMappingTable: (state, action: PayloadAction<SkuGroupMappingTable>) => {
      const mappedSelectedGroups = convertMappingTableToSelectedGroups({
        isDeletedGrouping: state.isDeletedGrouping,
        editMode: state.editMode,
        skuGroupMappingTable: action.payload,
      });

      const sortedInitialSelectedGroups = sortDefaultSelectedGroup(mappedSelectedGroups);

      //* set prime sku test level checkbox here
      const initialPrimeSkuTestLevelCheckboxes = convertMappingTableToPrimeTestLevelCheckboxes({
        editMode: state.editMode,
        isQualityFlow: state.isQualityFlow,
        skuGroupMappingTable: action.payload,
      });

      return {
        ...state,
        skuGroupMappingTable: action.payload,
        selectedGroups: sortedInitialSelectedGroups,
        primeSkuTestLevelCheckboxes: initialPrimeSkuTestLevelCheckboxes,
        selectAsPrimeCheckboxes: state?.editMode
          ? convertMappingTableToSelectAsPrimeCheckboxes({ skuGroupMappingTable: action.payload })
          : state?.selectAsPrimeCheckboxes,
      };
    },
    //* update primeSkuTestLevelCheckboxes
    setUpdatePrimeSkuTestLevelCheckbox: (
      state,
      action: PayloadAction<{
        selectedWicNumber: string;
        testLevel: TestLevel;
        checked: boolean | null;
      }>,
    ) => {
      const isChecked = action?.payload?.checked === true;

      const updatedSelectedGroups = isChecked
        ? assignSelectedGroupTestLevel({
            testLevel: action?.payload?.testLevel,
            optionWicNumber: action?.payload?.selectedWicNumber,
            selectedGroupWicNumber: action?.payload?.selectedWicNumber,
            selectedGroups: state?.selectedGroups,
          })
        : removeAllSelectedPrimeInSelectedGroupTestLevel({
            editMode: state?.editMode,
            testLevel: action?.payload?.testLevel,
            selectedGroupWicNumber: action?.payload?.selectedWicNumber,
            selectedGroups: state?.selectedGroups,
          });

      return {
        ...state,
        primeSkuTestLevelCheckboxes: updatePrimeSkuTestLevelCheckbox({
          ...action.payload,
          primeSkuTestLevelCheckboxesState: state.primeSkuTestLevelCheckboxes,
        }),
        // update selected group test level in all markets
        selectedGroups: updatedSelectedGroups,
      };
    },
    //* set prime sku masterWicId to selected group test level
    setAssignSelectedGroupTestLevel: (
      state,
      action: PayloadAction<{
        testLevel: TestLevel;
        optionWicNumber: string;
        selectedWicNumber: string;
      }>,
    ) => {
      const isSelectedCheckboxPrimeSku = Object.keys(state.primeSkuTestLevelCheckboxes).includes(
        action.payload.selectedWicNumber,
      );
      const targetSelectedGroup = state.selectedGroups.find(
        (group) => group.filters.wicNumber === action.payload.selectedWicNumber,
      );
      const isPrimeItself =
        targetSelectedGroup?.filters.wicNumber === action.payload.optionWicNumber;

      return {
        ...state,
        selectedGroups: assignSelectedGroupTestLevel({
          testLevel: action?.payload?.testLevel,
          optionWicNumber: action?.payload?.optionWicNumber,
          selectedGroupWicNumber: action?.payload?.selectedWicNumber,
          selectedGroups: state?.selectedGroups,
        }),
        //* check prime sku test level if selected itself
        primeSkuTestLevelCheckboxes: isSelectedCheckboxPrimeSku
          ? updatePrimeSkuTestLevelCheckbox({
              testLevel: action?.payload?.testLevel,
              selectedWicNumber: action?.payload?.selectedWicNumber,
              checked: isPrimeItself,
              primeSkuTestLevelCheckboxesState: state.primeSkuTestLevelCheckboxes,
            })
          : state.primeSkuTestLevelCheckboxes,
      };
    },
    //* remove prime sku masterWicId in selected group test level
    setRemoveSelectedGroupTestLevel: (
      state,
      action: PayloadAction<{
        testLevel: TestLevel;
        selectedWicNumber: string;
      }>,
    ) => {
      const isSelectedCheckboxPrimeSku = Object.keys(state?.primeSkuTestLevelCheckboxes).includes(
        action?.payload?.selectedWicNumber,
      );

      return {
        ...state,
        selectedGroups: removeAllSelectedPrimeInSelectedGroupTestLevel({
          editMode: state?.editMode,
          testLevel: action?.payload?.testLevel,
          selectedGroupWicNumber: action?.payload?.selectedWicNumber,
          selectedGroups: state?.selectedGroups,
        }),
        //* uncheck prime sku test level if selected itself
        primeSkuTestLevelCheckboxes: isSelectedCheckboxPrimeSku
          ? updatePrimeSkuTestLevelCheckbox({
              testLevel: action?.payload?.testLevel,
              selectedWicNumber: action?.payload?.selectedWicNumber,
              checked: false,
              primeSkuTestLevelCheckboxesState: state.primeSkuTestLevelCheckboxes,
            })
          : state.primeSkuTestLevelCheckboxes,
      };
    },
    setUpdateSelectAsPrimeCheckbox: (
      state,
      action: PayloadAction<{
        selectedWicNumber: string;
        checked: boolean;
      }>,
    ) => {
      const updatedSkuGroupMappingTable = updateSelectAsPrimeInSkuGroupMappingTable({
        selectedWicNumber: action.payload?.selectedWicNumber,
        checked: action.payload?.checked,
        skuGroupMappingTable: state?.skuGroupMappingTable,
      });

      const updatedSelectedGroups = editSelectAsPrimeSelectedGroups({
        checked: action?.payload?.checked,
        isDeletedGrouping: state.isDeletedGrouping,
        selectedGroups: state.selectedGroups,
        selectedGroupWicNumber: action.payload?.selectedWicNumber,
      });

      return {
        ...state,
        selectAsPrimeCheckboxes: updateSelectAsPrimeCheckbox({
          selectedWicNumber: action?.payload?.selectedWicNumber,
          checked: action?.payload?.checked,
          selectAsPrimeCheckboxes: state?.selectAsPrimeCheckboxes,
        }),
        primeSkuTestLevelCheckboxes: updatePrimeTestLevelCheckboxes({
          editMode: state.editMode,
          isQualityFlow: state.isQualityFlow,
          checked: action?.payload?.checked,
          selectedWicNumber: action?.payload?.selectedWicNumber,
          primeSkuTestLevelCheckboxes: state?.primeSkuTestLevelCheckboxes,
          skuGroupMappingTable: updatedSkuGroupMappingTable,
        }),
        skuGroupMappingTable: updatedSkuGroupMappingTable,
        selectedGroups: updatedSelectedGroups,
      };
    },
  },
});

export const {
  setOpenFillingActionModal,
  setFillingActionData,
  setSkuGroupingFilters,
  setApplicableTestLevel,
  setCombinedTaskByWicNumber,
  setDeletedGrouping,
  setEditMode,
  setExistingPrimeFilters,
  setInitialTasks,
  setSelectedGroupData,
  setFilteredSelectedGroupData,
  setAssignSelectedGroupTestLevel,
  setRemoveSelectedGroupTestLevel,
  setSkuGroupMappingTable,
  setUpdatePrimeSkuTestLevelCheckbox,
  setUpdateSelectAsPrimeCheckbox,
} = skugroupingSlice.actions;

export default skugroupingSlice.reducer;
